import {
  Arrange,
  Button,
  Container,
  FormField,
  Link,
  Pill,
  Spacer,
  Split,
  SplitSection,
  SvgArrowRight,
  SvgLaunch,
  SvgVideoCam,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import _capitalize from 'lodash/capitalize';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showErrorBar } from 'actions/errorBar';
import { ERROR, SUCCESS } from 'constants/error-severities';
import {
  DISABLE_EXTENSION_DOWNLOAD,
  EXP_SDK_IN_ONBOARDING,
} from 'constants/featureFlag';
import {
  ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED as ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED_METRIC,
  ONBOARDING_STEP_COMPLETE,
} from 'constants/metrics';
import { WORK } from 'constants/useCasePlans';
import { incrementMetric } from 'utilities/metrics';
import { isSdkSupported as importedIsSdkSupported } from 'utilities/sdk-support/isSdkSupported';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useGetUserOnboardingPersonaInfo } from 'hooks/workspaceOnboarding';

import * as analytics from '../../../../utilities/analytics';
import sendDownloadLoomEmail from './sendDownloadLoomEmail.graphql';
import { DOWNLOAD_OPTIONS_STEP } from '../../constants';
import { FixedMobileFooter, MobileScrollContainer } from '../../cta';
import { PageHeader } from '../../headers';
import {
  COMPLETED_ONBOARDING_STEP,
  EXTENSION_INSTALL_CLICKED,
  ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED as ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED_EVENT,
  ONBOARDING_MOBILE_EMAIL,
} from '../../../../constants/events';
import { PRIMARY_WEB_APP } from '../../../../constants/onboarding';
import {
  CHROME_EXT_WEBSTORE_URL,
  MY_VIDEOS_PAGE,
} from '../../../../constants/routes';
import { useExtensionInstalled } from '../../../../hooks/devices';
import { isAndroid, isMac, isMobile } from '../../../../utilities/device';
import {
  fetchLinkByPlatform,
  getSupportedPlatformType,
  MAC_SUPPORT,
  WIN_SUPPORT,
} from '../../../../utilities/download-desktop';
import { getGraphQLClient } from '../../../../utilities/graphql';
import { email as validateEmail } from '../../../../../../shared/validate';

import DownloadDesktopButton from 'components/download-desktop-button';
import DownloadDesktopDropdown from 'components/download-desktop-dropdown';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import { SDK_IN_ONBOARDING } from 'components/record-button';
import { SDK } from 'components/record-button/constants';

const RecordButton = React.lazy(() => import('components/record-button'));

const CHROME_EXTENSION = 'Chrome extension';

const RecorderInstallCard = ({
  header,
  description,
  primaryAction,
  isBeta = false,
}) => {
  return (
    <Container
      backgroundColor="grey1"
      radius="medium"
      padding="large"
      maxWidth={56.25}
    >
      <div className="flex justify:spaceBetween">
        <Text color="grey8" fontWeight="medium" size="large" width="100%">
          {header}
        </Text>
        {isBeta && (
          <Pill color="grey8" backgroundColor="grey3">
            Beta
          </Pill>
        )}
      </div>

      <Spacer top="small" bottom="medium">
        <Text color="bodyDimmed">{description}</Text>
      </Spacer>
      {primaryAction}
    </Container>
  );
};

const InstallChromeExtensionCard = ({
  hasExtensionInstalled,
  onDownloadClick,
}) => {
  const header = 'Install Chrome extension';
  const description = 'Basic recording needs and fast set up.';

  const disableExtensionDownload = useFlagIsActivated({
    flag: DISABLE_EXTENSION_DOWNLOAD,
    activationValues: [true],
  });

  const primaryAction = (
    <div>
      <Button
        icon={<SvgLaunch />}
        variant="primary"
        disabled={disableExtensionDownload ? true : hasExtensionInstalled}
        href={CHROME_EXT_WEBSTORE_URL}
        htmlTag={'a'}
        target="_blank"
        onClick={() => {
          analytics.track(EXTENSION_INSTALL_CLICKED, {
            where: window.location.pathname.replace('/', ''),
            is_simiplified_onboarding: true,
          });

          onDownloadClick(CHROME_EXTENSION, CHROME_EXT_WEBSTORE_URL);
        }}
      >
        {hasExtensionInstalled ? 'Currently installed' : 'Install extension'}
      </Button>
      {disableExtensionDownload && (
        <>
          <Spacer top="small" />
          <Text color="danger" size="small">
            We’re working through an issue with our Chrome extension. Please use
            our desktop recorder instead.
          </Text>
        </>
      )}
    </div>
  );

  return (
    <RecorderInstallCard
      header={header}
      description={description}
      primaryAction={primaryAction}
    />
  );
};

const InstallDesktopAppCard = ({ onDownloadClick }) => {
  const header = 'Download Desktop app';
  const description = 'Premium features like camera enhancements and HD video.';

  const platform = getSupportedPlatformType();

  const primaryAction = (
    <DownloadDesktopButton
      buttonText="Download app"
      afterOnClick={downloadLink => {
        analytics.track('"Finish Installing" Click', {
          platform,
          is_simiplified_onboarding: true,
        });

        onDownloadClick(platform, downloadLink);
      }}
    />
  );

  return (
    <RecorderInstallCard
      header={header}
      description={description}
      primaryAction={primaryAction}
    />
  );
};

const SDKInOnboardingCard = () => {
  const header = 'Try out recording!';
  const description = 'Lightweight recorder on Loom.com.';

  const primaryAction = (
    <Suspense fallback={null}>
      <RecordButton priorityList={[SDK]} source={SDK_IN_ONBOARDING}>
        <Button variant="record" icon={<SvgVideoCam />}>
          Try out recording
        </Button>
      </RecordButton>
    </Suspense>
  );

  return (
    <RecorderInstallCard
      header={header}
      description={description}
      primaryAction={primaryAction}
      isBeta={true}
    />
  );
};

function MobileDownloadEmailScreen({
  onSubmit,
  autoSendEmail,
  headerText,
  isStandaloneDownloadPage,
}) {
  const dispatch = useDispatch();

  const isLoggedIn = useSelector(state => state.user.is_logged_in);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState(null);

  const sendDownloadLoomEmailHandler = useCallback(
    async isAutoSend => {
      if (!isLoggedIn && !validateEmail(emailInput)) {
        setEmailError('Please enter a valid email address.');

        return;
      }

      try {
        const client = getGraphQLClient();

        const { data } = await client.mutate({
          mutation: sendDownloadLoomEmail,
          variables: {
            email: emailInput,
          },
        });

        // only show status bar if email wasn't auto send
        if (data.sendDownloadLoomEmail && !isAutoSend) {
          dispatch(showErrorBar(SUCCESS, 'Successfully sent download email'));
          analytics.track(ONBOARDING_MOBILE_EMAIL, {
            source: 'manual',
            is_simiplified_onboarding: true,
          });
        }
      } catch (e) {
        showErrorBar(ERROR, 'There was an error sending download email');
      }
    },
    [emailInput, isLoggedIn, dispatch]
  );

  // send email on load
  useEffect(() => {
    if (!autoSendEmail) {
      return;
    }

    sendDownloadLoomEmailHandler(true);

    analytics.track(ONBOARDING_MOBILE_EMAIL, {
      source: 'auto',
      is_simiplified_onboarding: true,
    });
  }, [sendDownloadLoomEmailHandler, autoSendEmail]);

  const mobileDevice = isAndroid ? 'Android' : 'iOS';
  const mobileDownloadLink = isAndroid
    ? 'https://play.google.com/store/apps/details?id=com.loom.android&hl=en_US&gl=US'
    : 'https://apps.apple.com/us/app/loom-screen-recording-video/id1474480829';

  let buttons = null;
  let hasCanSend = false;

  if (!isStandaloneDownloadPage) {
    buttons = (
      <FixedMobileFooter>
        <Split direction="column" justifyContent="flex-end" height="100%">
          <SplitSection width="100%">
            <TextButton
              className="width:full mb:small mt:auto"
              size="medium"
              onClick={() => sendDownloadLoomEmailHandler(false)}
            >
              Resend email
            </TextButton>
          </SplitSection>

          <SplitSection width="100%">
            <Button
              variant="primary"
              size="large"
              onClick={onSubmit}
              hasFullWidth
              icon={<SvgArrowRight />}
              iconPosition="right"
            >
              Continue to Workspace
            </Button>
          </SplitSection>
        </Split>
      </FixedMobileFooter>
    );
  } else if (isStandaloneDownloadPage && isLoggedIn) {
    buttons = (
      <Button
        variant="primary"
        size="large"
        onClick={() => sendDownloadLoomEmailHandler(false)}
        hasFullWidth
      >
        Resend email
      </Button>
    );
  } else {
    hasCanSend = true;
    buttons = (
      <Split direction="column" gap={2} width="100%">
        <SplitSection width="100%">
          <Spacer top={7} />
          <FormField
            label="Your email"
            labelFor="email"
            errorMessage={emailError}
          >
            <TextInput
              placeholder="Your email"
              id="first"
              value={emailInput}
              onChange={e => {
                setEmailInput(e.target.value);
                setEmailError(null);
              }}
              hasError={Boolean(emailError)}
            />
          </FormField>
        </SplitSection>
        <SplitSection width="100%">
          <Button
            variant="primary"
            size="medium"
            onClick={() => sendDownloadLoomEmailHandler(false)}
            hasFullWidth
          >
            Send email
          </Button>
        </SplitSection>
      </Split>
    );
  }

  return (
    <Container height="100%">
      <MobileScrollContainer
        offset="6rem"
        isMobile={isMobile && !isStandaloneDownloadPage}
      >
        <Spacer left={1} right={1}>
          <PageHeader text={headerText} />

          <Spacer top={3} bottom={5}>
            <Text size="large">
              We {hasCanSend ? 'can send' : 'sent'} you an email with links to
              download the Chrome extension or the Desktop app on your computer
              or laptop.
              <br />
              <br />
              On the go? Check out Loom for mobile.
            </Text>
            <Text size="large" color="blurple">
              <a href={mobileDownloadLink} color="blurple">
                Download Loom for {mobileDevice}.
              </a>
            </Text>
          </Spacer>
        </Spacer>
      </MobileScrollContainer>

      {buttons}
    </Container>
  );
}

export const AlternativeLink = ({
  platform,
  setDownloadingPlatform,
  setTryAgainLink,
}) => {
  const [windowsDownloadLink, setWindowsDownloadLink] = useState(null);

  useEffect(() => {
    fetchLinkByPlatform(WIN_SUPPORT).then(windowsLinks => {
      setWindowsDownloadLink(windowsLinks[0]);
    });
  }, []);

  return (
    <>
      {platform === MAC_SUPPORT && (
        <DownloadDesktopDropdown
          inlineTrigger={true}
          afterOnClick={chosenMacLink => {
            setDownloadingPlatform(MAC_SUPPORT);
            setTryAgainLink(chosenMacLink);
          }}
          trigger={<Link>{_capitalize(MAC_SUPPORT)}</Link>}
        />
      )}
      {platform === WIN_SUPPORT && (
        <Link
          href={windowsDownloadLink}
          onClick={() => {
            setDownloadingPlatform(WIN_SUPPORT);
            setTryAgainLink(windowsDownloadLink);
          }}
        >
          {_capitalize(WIN_SUPPORT)}
        </Link>
      )}{' '}
      {platform === CHROME_EXTENSION && (
        <Link
          href={CHROME_EXT_WEBSTORE_URL}
          target="_blank"
          onClick={() => {
            setDownloadingPlatform(CHROME_EXTENSION);
            setTryAgainLink(CHROME_EXT_WEBSTORE_URL);
          }}
        >
          {_capitalize(CHROME_EXTENSION)}
        </Link>
      )}
    </>
  );
};

function DownloadingClient({
  platform,
  onSubmit = () => {},
  isStandaloneDownloadPage,
  initialDownloadLink,
}) {
  const [downloadingPlatform, setDownloadingPlatform] = useState(platform);
  const [platformAlternative, setAlternativePlatform] = useState(null);
  const [secondAlternative, setSecondAlternative] = useState(null);
  const [tryAgainLink, setTryAgainLink] = useState(initialDownloadLink);

  // Update alternative platform according to the current selected download option
  useEffect(() => {
    switch (downloadingPlatform) {
      case CHROME_EXTENSION:
        setAlternativePlatform(isMac ? MAC_SUPPORT : WIN_SUPPORT);
        setSecondAlternative(isMac ? WIN_SUPPORT : MAC_SUPPORT);
        break;
      case MAC_SUPPORT:
        setAlternativePlatform(WIN_SUPPORT);
        setSecondAlternative(CHROME_EXTENSION);
        break;
      case WIN_SUPPORT:
        setAlternativePlatform(MAC_SUPPORT);
        setSecondAlternative(CHROME_EXTENSION);
        break;
      default:
        break;
    }
  }, [downloadingPlatform]);

  // Rendering helpers
  const isDownloadingExtension = downloadingPlatform === CHROME_EXTENSION;

  const header = isDownloadingExtension
    ? 'Installing Loom Chrome Extension'
    : `Downloading Loom Desktop App for ${_capitalize(downloadingPlatform)}`;

  return (
    <Arrange autoFlow="row">
      <PageHeader text={header} />
      <div className="mb:large mt:large md-mb:xlarge">
        <Text size="large">
          Having issues?{' '}
          <Link
            href={tryAgainLink}
            onClick={() => setDownloadingPlatform(downloadingPlatform)}
            target="_blank"
          >
            Try again
          </Link>
          . Looking for{' '}
          <AlternativeLink
            platform={platformAlternative}
            setDownloadingPlatform={setDownloadingPlatform}
            setTryAgainLink={setTryAgainLink}
          />{' '}
          <span>
            or{' '}
            <AlternativeLink
              platform={secondAlternative}
              setDownloadingPlatform={setDownloadingPlatform}
              setTryAgainLink={setTryAgainLink}
            />
          </span>
          ?
        </Text>
      </div>

      {!isStandaloneDownloadPage && (
        <div>
          <Button
            size="large"
            icon={<SvgArrowRight />}
            iconPosition="right"
            variant="primary"
            onClick={onSubmit}
          >
            Continue to Workspace
          </Button>
        </div>
      )}
    </Arrange>
  );
}

export function DownloadOptionsWithButtons(props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLink, setDownloadLink] = useState();

  // Handler for "Continue" button
  const submitHandler = () => {
    if (!props.isStandaloneDownloadPage) {
      analytics.track(COMPLETED_ONBOARDING_STEP, {
        step: DOWNLOAD_OPTIONS_STEP,
        action: 'continue-or-skip',
        is_simiplified_onboarding: true,
        source: PRIMARY_WEB_APP,
      });

      incrementMetric(ONBOARDING_STEP_COMPLETE, {
        step: DOWNLOAD_OPTIONS_STEP,
        source: PRIMARY_WEB_APP,
      });
    }

    props.nextClickHandler();
  };

  const onDownloadClick = (platform, activeDownloadLink) => {
    if (!props.isStandaloneDownloadPage) {
      analytics.track(COMPLETED_ONBOARDING_STEP, {
        step: DOWNLOAD_OPTIONS_STEP,
        action: 'download-click',
        platform,
        is_simiplified_onboarding: true,
        source: PRIMARY_WEB_APP,
      });

      incrementMetric(ONBOARDING_STEP_COMPLETE, {
        step: DOWNLOAD_OPTIONS_STEP,
        source: PRIMARY_WEB_APP,
      });
    }

    setIsDownloading(platform);
    setDownloadLink(activeDownloadLink);
  };

  return (
    <Container height="100%">
      {!isDownloading && (
        <DownloadOptions
          {...props}
          onSubmit={submitHandler}
          onDownloadClick={(platform, activeDownloadLink) =>
            onDownloadClick(platform, activeDownloadLink)
          }
        />
      )}
      {isDownloading && (
        <DownloadingClient
          {...props}
          platform={isDownloading}
          initialDownloadLink={downloadLink}
          onSubmit={submitHandler}
        />
      )}
    </Container>
  );
}

function DownloadOptions({
  isOnboarding,
  isStandaloneDownloadPage,
  onSubmit = () => {},
  onDownloadClick = () => {},
}) {
  const hasExtensionInstalled = useExtensionInstalled();
  const personaObject = useGetUserOnboardingPersonaInfo();
  const [isSdkSupported, setIsSdkSupported] = useState(false);

  useEffect(() => {
    if (!isMobile && !isStandaloneDownloadPage) {
      analytics.track(ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED_EVENT, {
        is_simiplified_onboarding: true,
        source: PRIMARY_WEB_APP,
      });

      incrementMetric(ONBOARDING_DOWNLOAD_RECORDER_SCREEN_VIEWED_METRIC, {
        source: PRIMARY_WEB_APP,
      });
    }
  }, [isStandaloneDownloadPage]);

  useEffect(() => {
    (async () => {
      const { supported } = await importedIsSdkSupported();

      setIsSdkSupported(supported);
    })();
  }, []);

  const headerText = isStandaloneDownloadPage
    ? 'Download Loom'
    : 'Record your first loom';

  const experimentAllowed = useCallback(() => {
    const reasons = [];

    if (!isSdkSupported) {
      reasons.push('Browser is not supported by the SDK');
    }

    if (isStandaloneDownloadPage) {
      reasons.push('User is not on the download step of the onboarding survey');
    }

    return {
      pass: isSdkSupported && !isStandaloneDownloadPage,
      failReason: reasons.join(', '),
    };
  }, [isSdkSupported, isStandaloneDownloadPage]);

  if (isMobile) {
    return (
      <MobileDownloadEmailScreen
        onSubmit={onSubmit}
        autoSendEmail={isOnboarding}
        headerText={headerText}
        isStandaloneDownloadPage={isStandaloneDownloadPage}
      />
    );
  }

  return (
    <>
      <div>
        <PageHeader text={headerText} />

        <Spacer top="6" bottom="0.375">
          <InstallChromeExtensionCard
            hasExtensionInstalled={hasExtensionInstalled}
            onDownloadClick={onDownloadClick}
          />
        </Spacer>

        <InstallDesktopAppCard onDownloadClick={onDownloadClick} />

        <FeatureFlaggedComponent
          flag={EXP_SDK_IN_ONBOARDING}
          activationValues={['variant']}
          eligibilityPreCheckFunction={experimentAllowed}
        >
          <Spacer top="5" bottom="medium">
            <SDKInOnboardingCard />
          </Spacer>
        </FeatureFlaggedComponent>

        {personaObject?.use_case_plan === WORK && (
          <Spacer top="2.5">
            <TextButton
              icon={<SvgArrowRight />}
              iconPosition="right"
              htmlTag="a"
              href={MY_VIDEOS_PAGE}
            >
              Continue to Workspace
            </TextButton>
          </Spacer>
        )}
      </div>
    </>
  );
}

export default DownloadOptions;
